import { gql } from "@apollo/client";

/*
 * The ban fields are written out in full in each document rather than shared as
 * a fragment. A fragment would need `__typename` to resolve — Apollo cannot tell
 * whether a type condition applies without it — and the component tests run
 * `MockedProvider` with `addTypename={false}`, which silently drops every
 * fragment field and hands the screen a row of undefineds instead of failing.
 */

/**
 * The banned accounts, newest ban first.
 *
 * Its own query rather than a `fetchUsers` filter: a ban also sets `isDeleted`,
 * and the paths that hide deleted members would hide the very people this screen
 * exists to un-ban.
 *
 * `search` is a server-side match over username, email, phone and name, so it is
 * applied on submit rather than per keystroke — every character would otherwise
 * be a query over the whole user table.
 *
 * `total` ignores both pagination and `search`, so it stays usable as the header
 * count. That also means it cannot be the pager's count while a search is
 * applied — see where it is passed to the table.
 *
 * `warning` is only ever set by `setUserBanned`; the list leaves it null. It is
 * selected anyway so a row the server later wants to annotate would show up
 * rather than being dropped here.
 *
 * No `pollInterval`. The list only changes when an admin changes it, and this
 * screen re-reads after each of its own writes.
 */
export const BANNED_USERS = gql`
  query BannedUsers($search: String, $skip: Int, $take: Int) {
    bannedUsers(search: $search, skip: $skip, take: $take) {
      users {
        userId
        username
        email
        phone
        isBanned
        isDeleted
        isAnonymized
        bannedAt
        banReason
        bannedBy
        warning
      }
      total
    }
  }
`;

/**
 * State of the enforcement switch, plus how many accounts are banned.
 *
 * `usingDefault` means the AppConfig row has never been written and the compiled
 * default (enforcement on) is what is running. Worth showing: an absent row and
 * a row set to `true` behave identically now, but not once somebody hand-edits
 * the table.
 *
 * Read straight from the database rather than through the 30s cache `BanGuard`
 * uses, so a read-back after a write is accurate — unlike the rate-limit and
 * country-blocking screens, where an immediate re-read returns stale values.
 */
export const USER_BAN_SETTINGS = gql`
  query UserBanSettings {
    userBanSettings {
      enabled
      bannedCount
      updatedAt
      usingDefault
    }
  }
`;
